import { useEffect, useState, type ReactNode } from 'react';
import { Loader2 } from 'lucide-react';
import { ScanIntro } from './ScanIntro';
import { ScanReady } from './ScanReady';
import { ScanStation } from './ScanStation';
import { ScanUnavailable } from './ScanUnavailable';
import { pageFile, scanLabel } from './scan-pages';
import { currentScanSession, onScanStopped } from './scan-session';
import { useScanAgent, type ScanAgentActions, type ScanAgentState } from './useScanAgent';
import './scan-sheet.css';

export interface ScanSheetProps {
  /** The pages are in the job: the sheet has done its work and closes. */
  onAdded?: () => void;
}

/** The headline the sheet keeps whatever Paige Scan's state (Dustin, 2026-10-03). */
export const SCAN_HEADLINE = 'Scan with your scanner';

/**
 * "Add pages from your scanner", one surface: a picture, one headline, then what this computer
 * needs. Paige Scan on this computer lists the scanners, scans, and hands the pages to this page,
 * which uploads them into the open job through its own session exactly as a file upload does. A
 * scanning station without a browser (Paige Scan's own scan window) is one quiet link away.
 */
export function ScanSheet({ onAdded }: ScanSheetProps): ReactNode {
  const [state, actions] = useScanAgent();
  const { adding, canAdd, addToJob } = useAddToJob(state, actions, onAdded);
  // Stop on the screen behind the sheet (CapturePoint's own button) stops Paige Scan as the sheet's Stop does.
  useEffect(() => onScanStopped(() => void actions.cancel()), [actions]);
  const [station, setStation] = useState(false);
  if (station) return <ScanStation onBack={() => setStation(false)} />;
  const ready = state.phase === 'ready' || state.phase === 'scanning';
  return (
    <div className="paige-scan-sheet">
      {state.phase === 'checking' && <Checking />}
      {state.phase === 'unavailable' && state.reason && (
        <ScanUnavailable reason={state.reason} problem={state.problem} onRetry={actions.retry} />
      )}
      {ready && (
        <>
          <ScanIntro
            title={SCAN_HEADLINE}
            art={state.pages.length === 0 && state.phase !== 'scanning'}
          />
          <ScanReady
            state={state}
            actions={actions}
            adding={adding}
            canAdd={canAdd}
            onAdd={addToJob}
          />
        </>
      )}
      <button type="button" className="paige-scan-station-link" onClick={() => setStation(true)}>
        Scanning station without a browser?
      </button>
    </div>
  );
}

/** The first look for Paige Scan: the picture and the headline, and a quiet line while it looks. */
function Checking(): ReactNode {
  return (
    <div className="paige-scan-setup">
      <ScanIntro title={SCAN_HEADLINE} />
      <p className="paige-scan-watching" role="status">
        <Loader2 size={13} className="paige-spin" aria-hidden="true" />
        Looking for Paige Scan…
      </p>
    </div>
  );
}

/** The pages into the job through the session the screen is waiting on; nothing without one. */
function useAddToJob(
  state: ScanAgentState,
  actions: ScanAgentActions,
  onAdded?: () => void,
): { adding: boolean; canAdd: boolean; addToJob: () => void } {
  const [adding, setAdding] = useState(false);
  const session = currentScanSession();
  const scanner = state.devices.find((d) => d.id === state.device)?.name ?? 'Scanner';
  const addToJob = () => {
    if (!session || state.pages.length === 0) return;
    setAdding(true);
    const files = state.pages.map((page, i) => pageFile(page, i + 1));
    session
      .addPages(files, scanLabel(scanner, files.length))
      .then(() => {
        actions.clearPages();
        onAdded?.();
      })
      .catch((error: unknown) =>
        actions.report(error instanceof Error ? error.message : 'The pages could not be added'),
      )
      .finally(() => setAdding(false));
  };
  return { adding, canAdd: session !== null, addToJob };
}
